<template>
<div class="orders-component">
  <div class="orders-component__content static-container">
    <div class="orders-component__list-section">
      <div class="orders-component__list-card" key="list">
        <header class="orders-component__list-header">
          <h3 class="orders-component__list-header-title">История заказов</h3>
          <div class="flex-grow invisible"></div>
          <router-link
            :to="{ name: 'dashboard' }"
            class="orders-component__list-header-link"
          ><i class="fal fa-arrow-left mr-1"/> В личный кабинет</router-link>
        </header>
        <TransitionExpand>
          <div
            v-if="orders"
            class="orders-component__list-table-wrapper"
          >
            <OrdersHistoryList
              :orders="orders"
              :get-status-info="getStatusInfo"
              class="orders-component__list-table"
            />
          </div>
          <Preloader v-else-if="loadingList"/>
          <p v-else class="text-center m-4">Нет заказов</p>
        </TransitionExpand>
      </div>
      <button
        v-if="!showAll"
        @click="showAll = true"
        class="orders-component__show-all-button btn btn-primary-outline btn-rounded"
        type="button"
      >Показать все</button>
    </div>
    <transition name="opacity" mode="out-in">
      <ProfileItemDetailsModal
        v-if="showDetails"
        @dismiss="$router.back()"
        :key="selectedOrder.id"
        class="orders-component__details-modal"
      >
        <div class="orders-component__details-card">
          <OrderDetailsHeader
            :name="selectedOrder.number"
            :order="selectedOrder"
            :status-info="getStatusInfo(selectedOrder)"
          />
          <OrderDetails
            :order="selectedOrder"
          />
        </div>
      </ProfileItemDetailsModal>
    </transition>
  </div>
</div>
</template>

<script>
import { mapState } from 'vuex';
import axios from '@/zenky';
import config from '@/config';
import withModals from '@/mixins/withModals';
import TransitionExpand from '@/components/AsyncTransitionExpand';
import InjectsSeo from "@/mixins/InjectsSeo";

export default {
  mixins: [withModals, InjectsSeo],

  metaInfo() {
    return {
      title: this.getFullPageTitle('Заказы'),
    }
  },

  components: {
    TransitionExpand,
    Preloader: () => import(/* webpackPreload: true */ '@/components/preloaders/Preloader'),
    OrdersHistoryList: () => import(/* webpackPreload: true */ '@/components/profile/orders/HistoryList'),
    ProfileItemDetailsModal: () => import('@/components/profile/ItemDetailsModal'),
    OrderDetails: () => import('@/components/profile/orders/Details'),
    OrderDetailsHeader: () => import('@/components/profile/orders/DetailsHeader'),
  },

  props: {
    id: {
      required: false,
      type: String,
    },
  },

  created() {
    this.$store.dispatch('orderStatuses/load');
    this.loadOrders();
  },

  computed: {
    ...mapState({
      orderStatuses: state => state.orderStatuses.orderStatuses,
      unsubmittedOrderId: state => state.order.order?.id,
    }),

    selectedOrder() {
      if (this.directlyLoadedOrder?.id === this.id) return this.directlyLoadedOrder;
      return this.id && this.orders?.find(o => o.id === this.id) || null;
    },

    showDetails() {
      return !!this.selectedOrder;
    },

    statusInfoMap() {
      const map = {};
      for (const s of this.orderStatuses) {
        map[s.id] = {
          text: s.name,
          style: {
            color: 'white',
            backgroundColor: s.background_color,
          },
        };
      }
      return map;
    },

    overlayModalActive() {
      return this.showDetails && !this.$store.getters['ui/isBreakpointActive']('lg');
    },
  },

  methods: {
    async loadOrders(all) {
      this.orders = null;
      this.loadingList = true;

      const req = axios.get('/profile/orders', {
        params: {
          // Combining both includes' so we don't have to load order data again when the user decides to open details
          // for a certain order.
          with: 'delivery_address,statuses_progress' + ',' + config.includes.ordersHistoryItem,
          page: 1,
          count: this.showAll ? 9999 : 6, // TODO pagination or something.
          order_by: '-submitted_at',
        }
      });

      this.orders = (await req).data.data;
      this.loadingList = false;
    },

    async loadOrderDirectly() {
      const req = axios.get(`/orders/${this.id}`, { params: { with: config.includes.ordersHistoryItem, } });

      this.directlyLoadedOrder = (await req).data.data;

      if (this.directlyLoadedOrder.id !== this.id) { // If it has changing during loading.
        this.directlyLoadedOrder = null;
      }
    },

    getStatusInfo(o) {
      if (!this.orderStatuses) {
        return {
          style: {
            backgroundColor: 'var(--color-grey-lighter)',
            color: 'var(--color-dark-lighter)',
          },
          text: '...',
        }
      }

      function findLast(arr, predicate) {
        for (let i = arr.length - 1; i >= 0; i--) {
          if (predicate(arr[i])) {
            return arr[i];
          }
        }

        return undefined;
      }

      // Not just using `o.current_status` because it may be a hidden status.
      const statusChange = findLast(o.statuses_progress, s => !!s.completed_at);

      if (!statusChange) {
        return {
          style: {
            backgroundColor: o.current_status.status.background_color,
            color: 'white',
          },
          text: o.current_status.status.name,
        }
      }

      return this.statusInfoMap[statusChange.status_id];
    },
  },
  watch: {
    showAll(n) {
      this.loadOrders();
    },
    unsubmittedOrderId(n, o) {
      // When the order has been submitted.
      if (o) this.loadOrders();
    },
    id: [
      {
        immediate: true,
        handler(n) {
          // E.g. if a link was opened directly.
          if (!!n && !this.selectedOrder) {
            this.loadOrderDirectly();
          }
        },
      },
      function(n) {
        if (n !== this.directlyLoadedOrder?.id) {
          this.directlyLoadedOrder = null;
        }
      },
    ],
  },
  data() {
    return {
      orders: null,
      loadingList: false,
      directlyLoadedOrder: null,
      showAll: false,
      modalDescriptors: [
        {
          isActive: 'overlayModalActive',
          id: this,
        },
      ],
    };
  },
}
</script>

<style lang="less">
@import './common';
@layout-breakpoint-name: lg; @layout-breakpoint: theme('screens.lg');

@r: orders-component;
@page-padding: theme('padding.2');
@card-margin: theme('margin.2');
.@{r} {
  padding: @page-padding;
  background: @page-background;
  &__content {
    @apply flex relative;
  }
  &__list-section {
    flex-grow: 4;
    flex-basis: 0;
    margin: @card-margin;
    max-width: calc(@card-max-width + 80px);
  }
  &__list-card:extend(.profile__card) {
    @apply overflow-hidden; // For rounded borders
    max-width: unset;
  }
  &__list-header:extend(.profile__card-header) {
  }
  &__list-header-title:extend(.profile__card-header-title) {}
  &__list-header-link:extend(.profile__card-header-button) {}
  &__list-table-wrapper {
    @apply overflow-x-auto;
  }
  &__list-table {
    @apply min-w-full;
  }
  &__show-all-button {
    @apply my-3;
  }
  &__details-modal {
    padding: @page-padding;
    flex-grow: 3;
    flex-basis: 0;
  }
  &__details-card:extend(.profile__card) {
    @apply relative // So it overlaps the mask
      flex flex-col; // So if it's a modal, the content does not overflow the container.
    min-height: 0; // So it doesn't overflow the container;
    margin: @card-margin;
  }
}

@screen @layout-breakpoint-name {
  .@{r} {
    &__details-modal {
      @apply p-0;
    }
    &__details-card {
      @apply sticky;
      top: calc(var(--header-height) + @card-margin + @page-padding);
      max-height: calc(100vh - var(--header-height) - var(--bottom-nav-bar-height) - 2 * (@card-margin + @page-padding));
    }
  }
}
</style>
